import type { QueryClient } from '@tanstack/react-query';

import type { Watch } from '../../../core/changes/watches';
import { useAuth } from '../../auth/hooks/useAuth';
import { useTimezoneSynced } from '../../auth/hooks/useTimezoneReconciliation';
import type { PlanRange } from '../types/planner';

export const plannerKeys = {
  all: ['planner'] as const,
  plan: (range?: PlanRange) => ['planner', 'plan', range?.from ?? null, range?.to ?? null] as const,
  notPlanned: () => ['planner', 'not-planned'] as const,
  nextUp: (classId: string) => ['planner', 'next-up', classId] as const,
  classTasks: (classId: string) => ['planner', 'class-tasks', classId] as const,
  task: (taskId: string) => ['planner', 'details', 'task', taskId] as const,
  assignment: (studentAssignmentId: string) => ['planner', 'details', 'assignment', studentAssignmentId] as const,
  events: (range: PlanRange) => ['planner', 'events', range.from, range.to] as const,
  momentum: () => ['planner', 'momentum'] as const,
  settings: () => ['planner', 'settings'] as const,
  timer: () => ['planner', 'timer'] as const,
};

export function plannerWatches(userId: string | undefined): Watch[] {
  if (!userId) return [];
  return [
    { anchor: { type: 'learner', id: userId }, include: 'subtree' },
    { anchor: { type: 'user', id: userId }, include: 'subtree' },
  ];
}

export function usePlannerQueryScope(): { userId: string | undefined; ready: boolean } {
  const { user } = useAuth();
  const synced = useTimezoneSynced();
  return { userId: user?.id, ready: !!user?.id && synced };
}

export function refreshPlannerLists(queryClient: QueryClient, keep?: readonly unknown[]): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: plannerKeys.all,
    predicate: (query) => !keep || JSON.stringify(query.queryKey) !== JSON.stringify(keep),
  });
}
